import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { BleDevice } from '@capacitor-community/bluetooth-le';
import { ModalController } from '@ionic/angular';
import { InputAppClientDBFacade } from '@monorepo/core-data';
import { BleService, DevicesService } from '@monorepo/services';
import { TDeviceManagementComponentProps, TDevicesDB, TNFCDevice, TRfidDevice } from '@monorepo/types';
import { map, take } from 'rxjs/operators';
import { UpdateReadpowerModalComponent } from '../../device-management/update-readpower-modal/update-readpower-modal.component';
import { combineLatest, Observable, Subscription } from 'rxjs';

@Component({
  selector: 'monorepo-ble-mode',
  templateUrl: './ble-mode.component.html',
  styleUrls: ['./ble-mode.component.scss'],
})
export class BleModeComponent implements OnInit, OnDestroy {
  // true for ba-app as no configuration is available.
  @Input() deviceManagementProps: TDeviceManagementComponentProps = {
    showOnlyRfidOptions: false,
    showNfcConnectionOption: false
  }

  readPower: number = 500;
  backArrow = 'assets/common/go-back.svg';
  allAvailableRfidVendors: TRfidDevice['vendor'][] = ['NEWLAND', 'CHAINWAY', 'APULSETECH'];
  vendorToDisplayNameMap: Record<TRfidDevice['vendor'], string> = {
    NEWLAND: 'ble_mode.newlandPlugin',
    CHAINWAY: 'ble_mode.chainwayIntegrated',
    APULSETECH: 'ble_mode.apulsetechPlugin',
  };
  isMultiMode: boolean = true;
  useRfidScanMode: boolean = false;
  connectedVendor: TRfidDevice['vendor'] | null = null;
  allowZebraBleConnection: boolean = false;
  isZebraBleDeviceConnected: boolean = false;
  isNFCEnabled: boolean = false;
  connectedNFCDevice: TNFCDevice | null = null;

  subscriptions$: Subscription[] = [];

  constructor(
    public bleService: BleService,
    public modalCtrl: ModalController,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private devicesService: DevicesService,
  ) {}

  ngOnInit(): void {
    this.isNFCEnabled = this.deviceManagementProps.showNfcConnectionOption;
    const config$ = this.inputAppClientDBFacade.getConfigurations().pipe(take(1));
    const connectedDevice$ = this.devicesService.getConnectedDeviceInfo();
    type TCombinedResult = {
      config: any | null;
      res: TDevicesDB['connectedDeviceInfo'];
    };
    const combined$: Observable<TCombinedResult> = this.deviceManagementProps.showOnlyRfidOptions
      ? connectedDevice$.pipe(
          map((res) => ({
            config: null,
            res,
          })),
        )
      : combineLatest([config$, connectedDevice$]).pipe(
          map(([config, res]) => ({
            config,
            res,
          })),
        );

    const s1$ = combined$.subscribe(({ config, res }) => {
      if (config) {
        const { useRFIDScanMode, readPowerOfRFID: readPowerOfRFIDFromConfig, allowZebraBleConnection, isNFCEnabled } = config;
        this.useRfidScanMode = useRFIDScanMode;
        this.allowZebraBleConnection = allowZebraBleConnection;
        this.isNFCEnabled = isNFCEnabled;
        if (this.allowZebraBleConnection) this.devicesService.setZebraEnabled(true);

        if (!res.RFID) {
          this.readPower = readPowerOfRFIDFromConfig;
        }
      }

      if (res.RFID) {
        const { vendor, readPower: userSetDeviceReadPower } = res.RFID;
        this.connectedVendor = vendor;
        this.readPower = userSetDeviceReadPower;
      } else {
        this.connectedVendor = null;
      }
      
      if(res.NFC) {
        this.connectedNFCDevice = res.NFC;
      } else {
        this.connectedNFCDevice = null;
      }
    });

    const s2$ = this.devicesService.getZebraBleModeDeviceConnectionSubscription().subscribe((res) => {
      this.isZebraBleDeviceConnected = res.connectionState === 'CONNECTED';
    });
    this.subscriptions$.push(s1$, s2$);
  }

  ngOnDestroy(): void {
    this.subscriptions$.forEach((s) => s.unsubscribe());
  }

  async turnOnBluetoothAndLocation($event: any) {
    if ($event.detail.checked) {
      await this.bleService.enableBluetooth();
      const isLocation = await this.bleService.isLocationEnabled();
      if (!isLocation) await this.bleService.showLocationAlert();
    }
  }

  goBack() {
    this.modalCtrl.dismiss();
  }

  async scanDevices() {
    await this.bleService.updateAvailableDevicesList();
  }

  connectToDevice(bleDevice: BleDevice) {
    this.bleService.connectToDeviceAndStartNotifications(bleDevice, false);
  }

  disconnectToDevice(bleDevice: BleDevice) {
    this.bleService.onUserDisconnectDevice(bleDevice.deviceId);
  }

  unpairDevice(bleDevice: BleDevice) {
    this.bleService.unpairDevice(bleDevice.deviceId);
  }

  handleConnectToVendorRfid(vendor: TRfidDevice['vendor']) {
    this.devicesService.connectToRfidDevice({
      type: "VENDOR",
      vendor,
      readPower: this.readPower
    });
  }

  async handleDisconnectVendorBasedRfid() {
    await this.devicesService.disconnectRfidDevice("VENDOR");
  }

  handleConnectToNfc() {
    this.devicesService.connectToRfidDevice({
      type: "NFC"
    })
  }

  async handleDisconnectNfc() {
    await this.devicesService.disconnectRfidDevice("NFC");
  }

  async openUpdateReadpowerModal() {
    if (!this.connectedVendor) return;
    const modal = await this.modalCtrl.create({
      component: UpdateReadpowerModalComponent,
      backdropDismiss: false,
      cssClass: 'update-readpower-modal',
      componentProps: this.#getComponentPropsForReadpowerUpdate(),
    });
    modal.present();
    modal.onDidDismiss().then((res) => {
      if (res.data !== null && res.data !== undefined) {
        this.devicesService.updateReadpower(res.data);
      }
    });
  }

  #getComponentPropsForReadpowerUpdate(): TUpdateReadpowerProps {
    const vendor = this.connectedVendor as TRfidDevice['vendor'];
    const propsWithReadpower: TUpdateReadpowerProps = {
      connectedVendor: vendor,
      ...this.devicesService.getMinimumAndMaximumSupportedRangeForTheVendorBasedRfidDevice(vendor),
      readpower: this.readPower,
    };
    return propsWithReadpower;
  }

  connectToZebraDeviceUsingBleMode() {
    this.devicesService.connectToZebraScannerUsingBleMode();
  }

  disconnectZebraBleModeDevice() {
    this.devicesService.disconnectZebraBleModeDevice();
  }
}

type TUpdateReadpowerProps = {
  connectedVendor: TRfidDevice['vendor'];
  minimumSupportedRange: number;
  maximumSupportedRange: number;
  readpower: number;
};
